import { NavLink } from 'react-router-dom';
import clsx from 'clsx';

import { Scopes, type Scope } from '../api/types';
import { useAuth } from '../auth/useAuth';
import { Logo } from './Logo';

interface NavItem {
  to: string;
  label: string;
  /** Required scope; entries the user can't access are hidden. */
  scope: Scope;
  icon: JSX.Element;
}

const NAV_ITEMS: ReadonlyArray<NavItem> = [
  {
    to: '/dashboard',
    label: 'Dashboard',
    scope: Scopes.DocumentsRead,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
        <path d="M3 13h8V3H3v10zm10 8h8V11h-8v10zM3 21h8v-6H3v6zm10-18v6h8V3h-8z" />
      </svg>
    ),
  },
  {
    to: '/dashboard/roi',
    label: 'ROI',
    scope: Scopes.DocumentsRead,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
        <path d="M3 17l6-6 4 4 7-7" />
        <path d="M17 7h4v4" />
      </svg>
    ),
  },
  {
    to: '/documents',
    label: 'Documentos',
    scope: Scopes.DocumentsRead,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
        <path d="M7 3h7l5 5v13H7z" />
        <path d="M14 3v5h5" />
      </svg>
    ),
  },
  {
    to: '/batches',
    label: 'Lotes',
    scope: Scopes.DocumentsWrite,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
        <path d="M4 7h16M4 12h16M4 17h16" />
        <path d="M8 4v3M8 10v3M8 15v3" />
      </svg>
    ),
  },
  {
    to: '/document-types',
    label: 'Tipos de documento',
    scope: Scopes.SchemasRead,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
        <path d="M4 6h16M4 12h16M4 18h10" />
      </svg>
    ),
  },
  {
    to: '/config',
    label: 'Configuración',
    scope: Scopes.ConnectionsRead,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
  },
];

/** Scope-filtered nav list — shared by the desktop sidebar and the mobile drawer. */
export function SidebarNav(): JSX.Element {
  const { hasScope } = useAuth();

  return (
    <nav className="flex-1 overflow-y-auto py-3">
      <ul className="space-y-1 px-2">
        {NAV_ITEMS.filter((item) => hasScope(item.scope)).map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-accent/10 text-accent'
                    : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900',
                )
              }
            >
              {item.icon}
              <span>{item.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function Sidebar(): JSX.Element {
  return (
    <aside className="hidden h-full w-60 flex-none border-r border-slate-200 bg-white md:flex md:flex-col">
      <div className="flex h-14 items-center border-b border-slate-200 px-4">
        <Logo size={26} />
      </div>
      <SidebarNav />
    </aside>
  );
}
